import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DISPLAY_HEADER_ICON_BACK,
  DISPLAY_PAGE_INFERENCE_FACTORS_ITEMS,
  PAGE_NAME_TOP,
  PAGE_NAME_REGISTER_SUBJECTNAME,
  WORKHOUR_SHORT,
  WORKHOUR_MEDIUM,
  WORKHOUR_LONG,
  NO_SELECTION,
  FILTER_ITEM,
  FILTER_CRITERIA,
  CATEGORY,
  WORK_HOUR,
} from "../../constants/common_constants";
import { InferenceFactorAccordionLists } from "../../features/components/inference_factors/ui_elements/InferenceFactorAccordionLists";
import { IInferenceFactorListType } from "../../features/components/inference_factors/types/inference_factor_type";
import {
  CategoryInfoListType,
  CategoryInfoType,
} from "../../features/components/inference_factors/types/category_info_type";
import { calcUtcDatetime } from "../../utils/basis_util";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import {
  useApi,
  useErrorDialog,
  useInitUsageLog,
  useSession,
} from "../../hooks";
import { useTranslation } from "react-i18next";
import { InferenceFactorLengend } from "../../features/components/inference_factors/ui_elements/InferenceFactorLegend";
import { InferenceFactorFilter } from "../../features/components/inference_factors/ui_elements/InferenceFactorFilter";
import { getCategory } from "../../features/api/dts_api_wrapper";
import Gauge from "../../icons/Gauge_L.svg";

//カラーコードのフォーマット
const COLOR_CODE_FORMAT =
  /^#?(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

export const InferenceFactorItem = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();

  // ローカルストレージから取得したデータを変換して格納する変数
  const [listData, setListData] = useState<IInferenceFactorListType>({
    inferenceFactor: [],
  });

  const [model, setModel] = useState<string>("");
  const [machineNumber, setMachineNumber] = useState<string>("");
  //凡例の表示状態を格納する変数
  const [isShowLegend, setIsShowLegend] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [filterItem, setFilterItem] = useState<string>("");
  const [filterCriteria, setFilterCriteria] = useState<string | number>("");
  const {
    dacModel,
    dacMachineNumber,
    dacInferenceFactor,
    dacLanguage,
  } = useDtsAppContext();

  //State、またはローカルストレージから言語を取得
  const language = dacLanguage || localStorage.getItem("language") || "en";
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
    ) || "";
  const displayPage = DISPLAY_PAGE_INFERENCE_FACTORS_ITEMS;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  const { handleApi } = useApi();

  // 利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  // セッション情報チェック
  const { handleCheckSession } = useSession("navigateToTopPage");

  //カテゴリ情報
  const [categoryInfoList, setCategoryInfoList] =
    useState<CategoryInfoListType>({ language: "", category: [] });

  useEffect(() => {
    handleInferenceFactorItemlInit();
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key ===
        `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
      ) {
        const authUser = localStorage.getItem(
          `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
        );
        if (!authUser) {
          console.log(`[${displayPage}]err: userId is unknown`);
          showErrorDialog(
            t("共通エラーポップ.エラー"),
            t("共通エラーポップ.通信エラーが発生しました。"),
            "navigateToTopPage",
          ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //ページ表示時に行うInferenceFactorItemの処理
  const handleInferenceFactorItemlInit = async () => {
    try {
      console.log("handleInferenceFactorItemlInit start.");

      // 初期状態としてコンテキストorローカルストレージからデータを取得
      getInitialInfo();

      //利用ログ登録
      await initUsageLog();

      //フィルター条件をlocalStorageから取得してstateに格納
      const _filterItem = localStorage.getItem(FILTER_ITEM) || "";
      setFilterItem(_filterItem);
      getFilterCriteria();

      //カテゴリ情報取得
      const infData =
        dacInferenceFactor || localStorage.getItem("inferenceFactor") || "";
      //推定要因項目からカテゴリIDを取得
      if (infData) {
        const tmpListData: IInferenceFactorListType = JSON.parse(infData);
        const categoryId: string[] = Array.from(
          new Set(
            tmpListData.inferenceFactor.map(
              (inferenceFactor) => inferenceFactor.issueFactor.categoryId,
            ),
          ),
        );

        //カテゴリ情報を取得するAPI
        const categoryInfoResult = await handleApi(
          getCategory,
          userId,
          categoryId,
          language,
        );
        if ("errorMessage" in categoryInfoResult) {
          console.log("errorMessage: ", categoryInfoResult.errorMessage);
          throw new Error("failed to get category");
        }

        const categoryInfoList: CategoryInfoListType = JSON.parse(
          JSON.stringify(categoryInfoResult.category),
        );
        //データが空かどうか判別, エラーダイアログ表示(その場にとどまる)
        if (!categoryInfoList.category.length)
          throw new Error("category is unknown");

        //カラーコードのバリデーションチェック
        validateColorCode(categoryInfoList);

        setCategoryInfoList(categoryInfoList);

        applyFilter();
      } else {
        //要因(Context、LocalStorageのどちらからも)取得失敗
        console.log("inferenceFactor is unknown");
        throw new Error("inferenceFactor is unknown");
      }
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      //getInitialInfo()で通信エラーになった場合
      if (
        err instanceof Error &&
        err.message === "inferenceFactor is unknown"
      ) {
        //要因取得失敗
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("推定要因項目画面.要因が見つかりません。"),
          "navigateToTopPage",
        ); //エラー", "要因が見つかりません。"(トップページに遷移する)
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        // カテゴリ取得失敗時もこのエラー
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage",
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      }
    }
  };

  //フィルタ条件をlocalStorageから取得して正しいフォーマットに変換する関数
  const getFilterCriteria = () => {
    const _filterCriteria = localStorage.getItem(FILTER_CRITERIA) || "";
    //フィルター条件の値によって分岐
    switch (_filterCriteria) {
      case String(WORKHOUR_SHORT):
        setFilterCriteria(Number(WORKHOUR_SHORT));
        break;
      case String(WORKHOUR_MEDIUM):
        setFilterCriteria(Number(WORKHOUR_MEDIUM));
        break;
      case String(WORKHOUR_LONG):
        setFilterCriteria(Number(WORKHOUR_LONG));
        break;
      default:
        setFilterCriteria(_filterCriteria);
        break;
    }
  };
  //カラーコードのバリデーションチェックを行う関数
  const validateColorCode = (categoryInfoList: CategoryInfoListType) => {
    //3桁or4桁or6桁or8桁の16進数のみOKとする
    const hasInvalid = categoryInfoList.category.some((category: any) => {
      const isFontColorMatch = COLOR_CODE_FORMAT.test(category.fontColor);
      const isBackColorMatch = COLOR_CODE_FORMAT.test(category.backColor);
      return !isFontColorMatch || !isBackColorMatch;
    });

    if (hasInvalid) {
      console.log("category validation error.");
      throw new Error("failed to get category");
    } else {
      console.log("color code is valid.");
    }
  };

  //Context or ローカルストレージからデータ取得
  const getInitialInfo = () => {
    try {
      //型式、機番、推定要因項目をContext or ローカルストレージから読み出す
      const _model = dacModel || localStorage.getItem("model") || "";
      const _machineNumber =
        dacMachineNumber || localStorage.getItem("machineNumber") || "";
      const infData =
        dacInferenceFactor || localStorage.getItem("inferenceFactor") || "";

      //型式
      if (_model) {
        setModel(_model);
      } else {
        console.log("model is unknown");
      }

      //機番
      setMachineNumber(_machineNumber);

      //要因
      if (infData) {
        setListData(JSON.parse(infData));
      } else {
        //要因(Context、LocalStorageのどちらからも)取得失敗
        console.log("inferenceFactor is unknown");
        throw new Error("inferenceFactor is unknown");
      }

      //要因以外のパラメーター不足(Context、LocalStorageのどちらからも)取得失敗
      // 機番は読み込めなくても良い
      if (!_model) {
        throw new Error("failed to load parameter.");
      }
    } catch (err) {
      throw err;
    }
  };

  //凡例押下時の処理
  const handleIsShowLegend = () => {
    setIsShowLegend(true);
  };

  //画面更新時or適用ボタン押下時の処理
  const applyFilter = () => {
    //フィルタをかける前の推定要因項目を取得
    const infData = localStorage.getItem("inferenceFactor") || "";
    const orgListData: IInferenceFactorListType = JSON.parse(infData);
    let newListData: IInferenceFactorListType = { inferenceFactor: [] };
    // リロード時はlocalStorageに保存されたフィルタ条件を読み込み
    const _filterItem =
      filterItem || localStorage.getItem(FILTER_ITEM) || NO_SELECTION;
    const _filterCriteria =
      filterCriteria || localStorage.getItem(FILTER_CRITERIA) || NO_SELECTION;
    //フィルタ項目が選択なしの場合
    if (_filterCriteria === NO_SELECTION || _filterItem === NO_SELECTION) {
      setListData(orgListData);
      //フィルタ項目がカテゴリの場合
    } else if (_filterItem === CATEGORY) {
      orgListData.inferenceFactor.forEach((inferenceFactor) => {
        if (_filterCriteria === inferenceFactor.issueFactor.categoryId) {
          newListData.inferenceFactor.push(inferenceFactor);
        }
      });
      setListData(newListData);
      //フィルタ項目が作業量の場合
    } else if (_filterItem === WORK_HOUR) {
      orgListData.inferenceFactor.forEach((inferenceFactor) => {
        if (
          Number(_filterCriteria) ===
          inferenceFactor.issueFactor.repairFactor.workload
        ) {
          newListData.inferenceFactor.push(inferenceFactor);
        }
      });
      setListData(newListData);
    }
    //localStorageにソート条件を格納する
    localStorage.setItem(FILTER_ITEM, String(_filterItem));
    localStorage.setItem(FILTER_CRITERIA, String(_filterCriteria));
    //フィルタメニューを閉じる
    setOpen(false);
  };

  // アコーディオンメニューをレンダリングする関数
  const renderInferenceFactorAccordion = () => {
    return listData.inferenceFactor.map((inferenceFactor) => {
      const categoryInfo: CategoryInfoType = categoryInfoList.category.find(
        (category: CategoryInfoType) =>
          inferenceFactor.issueFactor.categoryId === category.categoryId,
      ) as CategoryInfoType;

      return (
        <InferenceFactorAccordionLists
          inferenceFactor={inferenceFactor}
          key={inferenceFactor.issueFactor.label}
          categoryInfo={categoryInfo}
        >
          <InferenceFactorAccordionLists
            inferenceFactor={inferenceFactor}
            categoryInfo={categoryInfo}
          />
        </InferenceFactorAccordionLists>
      );
    });
  };

  return (
    // スクロール不可に設定
    <div className="flex flex-col h-svh overflow-hidden">
      {/* header */}
      <GeneralCommonHeader
        title={t("推定要因項目画面.推定要因項目")} //推定要因項目
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* header以下 */}
      <div className="flex flex-col m-4 h-full overflow-hidden">
        {/* 入力済項目 */}
        <div className="mb-2">
          <div className="pb-1 flex gap-2">
            <div className="shrink-0">
              {t("推定要因項目画面.型式")} :{/* 型式： */}
            </div>
            <div className="flex-1 min-w-0 break-words">{model}</div>
            {/* 凡例表示リンク */}
            <p className="text-sub-blue underline" onClick={handleIsShowLegend}>
              {t("推定要因項目画面.凡例")}
            </p>
          </div>
          <div className="pb-1 flex gap-2">
            <div className="shrink-0">
              {t("推定要因項目画面.機番")} :{/* 機番：  */}
            </div>
            <div className="flex-1 min-w-0 break-words">{machineNumber}</div>
          </div>
          {/* <p className="text-end pb-1">
            {t("推定要因項目画面.発生確率")}
          </p> */}
        </div>

        {/* 凡例ポップアップ */}
        {isShowLegend && (
          <InferenceFactorLengend
            categoryInfoList={categoryInfoList}
            setIsShowLegend={setIsShowLegend}
          />
        )}

        {/* フィルター・工数の凡例 */}
        <div className="flex items-start justify-between">
          {/* フィルター部分 */}
          <InferenceFactorFilter
            open={open}
            setOpen={setOpen}
            filterItem={filterItem}
            filterCriteria={filterCriteria}
            setFilterItem={setFilterItem}
            setFilterCriteria={setFilterCriteria}
            categoryInfoList={categoryInfoList}
            setCategoryInfoList={setCategoryInfoList}
            applyFilter={applyFilter}
          />

          {/* 工数凡例部分 */}
          <div className="flex items-end">
            <div className="flex flex-col gap-1 mb-[-3px] mr-[-16px]">
              <span className="text-[12px]">{t("推定要因項目画面.大")}</span>
              <span className="text-[12px]">{t("推定要因項目画面.小")}</span>
            </div>
            <div className="flex flex-col w-[65px] items-center">
              <p className="text-[12px] mt-[-12px] mb-1">
                {t("推定要因項目画面.工数")}
              </p>
              <img src={Gauge} alt="工数の凡例" />
            </div>
          </div>
        </div>

        <hr className="my-4 border-t border-black" />

        {/* フィードバック選択 */}
        <div className="h-full overflow-y-scroll overflow-x-hidden">
          {renderInferenceFactorAccordion()}

          <div className="flex justify-evenly mt-[40px]">
            {/* メイン画面に戻るボタン */}
              <button 
                className="items-center gap-1 border-2 border-main-red rounded-full bg-white w-[150px] py-2 overflow-hidden" 
                onClick={() => navigate(`/${PAGE_NAME_TOP}`)}>
                  <p className="font-bold text-[20px]">{t("推定要因項目画面.メイン画面へ")}</p>
              </button>
            {/* 次へボタン */}
              <button 
                className="items-center gap-1 border-2 border-main-red rounded-full bg-white w-[150px] py-2 overflow-hidden" 
                onClick={() => navigate(`/${PAGE_NAME_REGISTER_SUBJECTNAME}`)}>
                  <p className="font-bold text-[20px]">{t("推定要因項目画面.次へ")}</p>
              </button>
          </div>
        </div>
      </div>
    </div>
  );
};
